/* eslint-disable max-lines */
import { Box, Card, Grid, InputLabel, Typography, useTheme } from '@mui/material';
import { styled, useMediaQuery } from '@mui/system';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import parentIcon from '../../../images/Parent.svg';
import otherIcon from '../../../images/Other.svg';
import caeInfoIcon from '../../../images/caeInfoIcon.svg';
import grandparentIcon from '../../../images/Grandparent.svg';
import { CustomInput } from '../CustomInput/CustomInput';
import {
  BENEFICIARY_INFO,
  COLOR5,
  EMAIL,
  EN,
  INVALID_FORMAT_ERR,
  LEGAL_GUARDIAN,
  MANDATORY_FIELD_ERR,
  REQUIRED,
  TOOLTIP_ABC_BIRTH_PROOF,
  TOOLTIP_EDUCATIVA_BIRTH_PROOF,
} from '../../../constants';
import { commonUtility } from '../UtilityFunctions';
import { isABCProduct, isWebComponentFlow } from '../../commonCode';
import { CustomSelect } from '../CustomSelect/CustomSelect';
import BeneficiaryAddressInfo from './BeneficiaryAddressInfo';
import CommonDocumentUpload from '../../Sidebar/CommonDocumentUpload';
import CustomSwitch from '../Switch/CustomSwitch';
import { BeneficiaryCompStyles as classes } from './BeneficiaryCompStyles';

const BeneficiaryCardComp = (props) => {
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery('(max-width:1350px)');
  const baseContext = isWebComponentFlow();
  const {
    beneficiaryData,
    setBeneficiaryData,
    beneficiaryDataErrors,
    setBeneficiaryDataErrors,
    authorityProofUploadStatus,
    setAuthorityProofUploadStatus,
    birthProofUploadStatus,
    setBirthProofUploadStatus,
    authorityProofUploadData,
    setAuthorityProofUploadData,
    authorityProofUploadError,
    setAuthorityProofUploadError,
    birthProofUploadData,
    setBirthProofUploadData,
    birthProofUploadError,
    setBirthProofUploadError,
    addressInformation,
    setTaxUploadProgress,
    setTaxLinearProgress,
    setIsBeneficiaryScreenDirty,
    setIsScreenFiveDirty,
    setIsFundingScreenDirty,
    language,
    productName,
  } = baseContext;

  const [isDisabled, setIsDisabled] = useState(false);
  const defaultCountry = addressInformation?.defaultCountryBasedOnLocation;
  const themes = useTheme();
  const isTab = useMediaQuery(themes?.breakpoints?.down(900), {
    defaultMatches: true,
  });
  const isABC = isABCProduct(productName);

  useEffect(() => {
    const disabled = birthProofUploadData?.document || authorityProofUploadData?.document;
    setIsDisabled(disabled);
  }, [birthProofUploadData, authorityProofUploadData]);

  const removeDataOnToggle = () => {
    setBeneficiaryData((prevState) => ({
      ...prevState,
      caeData: { ...prevState?.caeData, emailAddress: '', emailRecipient: '' },
    }));
    setBeneficiaryDataErrors((prevState) => ({
      ...prevState,
      caeData: { ...prevState?.caeData, emailAddress: '', emailRecipient: '' },
    }));
    setAuthorityProofUploadData({});
    setAuthorityProofUploadError({});
    setAuthorityProofUploadStatus(null);
    setTaxUploadProgress((prev) => ({
      ...prev,
      'Proof of guardianship': 0,
      'Proof of custodianship': 0,
    }));
    setTaxLinearProgress((prev) => ({
      ...prev,
      'Proof of guardianship': false,
      'Proof of custodianship': false,
    }));
  };

  const handleRelationChange = (e, relation) => {
    setIsBeneficiaryScreenDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    if (beneficiaryData?.relationship !== relation) {
      setBeneficiaryData((prevState) => ({
        ...prevState,
        ...(!isABC && { legalGuardian: true }),
        otherRelation: '',
        addressInfo:
          relation === 'Parent' || isABC
            ? { ...prevState?.addressInfo, sameBeneficiaryAddress: true }
            : {
                sameBeneficiaryAddress: false,
                beneficiaryAddress1: '',
                beneficiaryAddress2: '',
                beneficiaryCity: '',
                beneficiaryCountry: defaultCountry,
                beneficiaryState: defaultCountry === 'VI' || defaultCountry === 'PR' ? 'N/A' : '',
                beneficiaryZipCode: '',
              },
        caeData: { ...prevState?.caeData, emailAddress: '', emailRecipient: '' },
      }));
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        legalGuardian: '',
        otherRelation: '',
        addressInfo: {
          beneficiaryAddress1: '',
          beneficiaryAddress2: '',
          beneficiaryCity: '',
          beneficiaryCountry: '',
          beneficiaryState: '',
          beneficiaryZipCode: '',
          beneficiaryMailingAddress1: '',
          beneficiaryMailingAddress2: '',
          beneficiaryMailingCity: '',
          beneficiaryMailingCountry: '',
          beneficiaryMailingState: '',
          beneficiaryMailingZipCode: '',
        },
        caeData: { ...prevState?.caeData, emailAddress: '', emailRecipient: '' },
      }));
      setBirthProofUploadData({});
      setBirthProofUploadError({});
      setBirthProofUploadStatus(null);
      setAuthorityProofUploadData({});
      setAuthorityProofUploadError({});
      setAuthorityProofUploadStatus(null);
      setTaxUploadProgress((prev) => ({
        ...prev,
        'Proof of guardianship': 0,
        'Proof of custodianship': 0,
        'Date of birth certificate': 0,
      }));
      setTaxLinearProgress((prev) => ({
        ...prev,
        'Proof of guardianship': false,
        'Proof of custodianship': false,
        'Date of birth certificate': false,
      }));
    }
    setBeneficiaryData((prevState) => ({
      ...prevState,
      relationship: relation,
    }));
    if (!relation) {
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        relationship: MANDATORY_FIELD_ERR,
      }));
    } else {
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        relationship: '',
      }));
    }
  };
  const handleAuthorityToggle = (e) => {
    setIsBeneficiaryScreenDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    removeDataOnToggle();
    if (e.target.checked === true) {
      setBeneficiaryData((prevState) => ({
        ...prevState,
        legalGuardian: true,
      }));
    } else if (e.target.checked === false) {
      setBeneficiaryData((prevState) => ({
        ...prevState,
        legalGuardian: false,
      }));
    }
  };
  const authorityToggleOnEnter = (input) => {
    setIsBeneficiaryScreenDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    removeDataOnToggle();
    if (input === 'legalGuardian') {
      const currentState = beneficiaryData?.legalGuardian;
      const toggledState = !currentState;
      if (toggledState === false) {
        setBeneficiaryData((prevState) => ({
          ...prevState,
          legalGuardian: false,
        }));
      }
      if (toggledState === true) {
        setBeneficiaryData((prevState) => ({
          ...prevState,
          legalGuardian: true,
        }));
      }
    }
  };
  const handleRecipientChange = (e, recipient) => {
    setIsBeneficiaryScreenDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    setBeneficiaryData((prevState) => ({
      ...prevState,
      caeData: { ...prevState?.caeData, emailRecipient: recipient },
    }));
    if (!recipient) {
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        caeData: { ...prevState?.caeData, emailRecipient: MANDATORY_FIELD_ERR },
      }));
    } else {
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        caeData: { ...prevState?.caeData, emailRecipient: '' },
      }));
    }
  };
  const handleEmailChange = (e) => {
    setIsBeneficiaryScreenDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    if (
      e.target.value.match(
        /^([a-zA-Z0-9_.-]+)@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.)|(([a-zA-Z0-9-]+\.)+))([a-zA-Z]{2,4}|\d{1,3})(\]?)$/
      )
    ) {
      const emailFirstPart = e.target.value.split('@');
      if (
        emailFirstPart[0].charAt(0) !== '.' &&
        emailFirstPart[0].charAt(0) !== '-' &&
        emailFirstPart[0].charAt(emailFirstPart[0].length - 1) !== '-' &&
        emailFirstPart[0].charAt(emailFirstPart[0].length - 1) !== '.' &&
        !e.target.value.match(/[.\-_]{2,}/)
      ) {
        setBeneficiaryData((prev) => ({
          ...prev,
          caeData: { ...prev?.caeData, emailAddress: e.target.value },
        }));
        setBeneficiaryDataErrors((prev) => ({
          ...prev,
          caeData: { ...prev?.caeData, emailAddress: '' },
        }));
      } else {
        setBeneficiaryData((prev) => ({
          ...prev,
          caeData: { ...prev?.caeData, emailAddress: e.target.value },
        }));
        setBeneficiaryDataErrors((prev) => ({
          ...prev,
          caeData: { ...prev?.caeData, emailAddress: INVALID_FORMAT_ERR },
        }));
      }
    } else if (e.target.value === '') {
      setBeneficiaryData((prev) => ({
        ...prev,
        caeData: { ...prev?.caeData, emailAddress: '' },
      }));
      setBeneficiaryDataErrors((prev) => ({
        ...prev,
        caeData: { ...prev?.caeData, emailAddress: MANDATORY_FIELD_ERR },
      }));
    } else {
      setBeneficiaryData((prev) => ({
        ...prev,
        caeData: { ...prev?.caeData, emailAddress: e.target.value },
      }));
      setBeneficiaryDataErrors((prev) => ({
        ...prev,
        caeData: { ...prev?.caeData, emailAddress: INVALID_FORMAT_ERR },
      }));
    }
  };
  const handleOtherRelationChange = (e) => {
    setIsBeneficiaryScreenDirty(true);
    setIsScreenFiveDirty(true);
    setIsFundingScreenDirty(true);
    const { value } = e.target;
    if (beneficiaryData?.otherRelation !== value) {
      setBeneficiaryData((prevState) => ({
        ...prevState,
        ...(!isABC && { legalGuardian: true }),
        addressInfo: isABC
          ? { ...prevState?.addressInfo, sameBeneficiaryAddress: true }
          : {
              sameBeneficiaryAddress: false,
              beneficiaryAddress1: '',
              beneficiaryAddress2: '',
              beneficiaryCity: '',
              beneficiaryCountry: defaultCountry,
              beneficiaryState: defaultCountry === 'VI' || defaultCountry === 'PR' ? 'N/A' : '',
              beneficiaryZipCode: '',
            },
        caeData: { ...prevState?.caeData, emailAddress: '', emailRecipient: '' },
      }));
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        legalGuardian: '',
        addressInfo: {
          beneficiaryAddress1: '',
          beneficiaryAddress2: '',
          beneficiaryCity: '',
          beneficiaryCountry: '',
          beneficiaryState: '',
          beneficiaryZipCode: '',
          beneficiaryMailingAddress1: '',
          beneficiaryMailingAddress2: '',
          beneficiaryMailingCity: '',
          beneficiaryMailingCountry: '',
          beneficiaryMailingState: '',
          beneficiaryMailingZipCode: '',
        },
        caeData: { ...prevState?.caeData, emailAddress: '', emailRecipient: '' },
      }));
      setBirthProofUploadData({});
      setBirthProofUploadError({});
      setBirthProofUploadStatus(null);
      setAuthorityProofUploadData({});
      setAuthorityProofUploadError({});
      setAuthorityProofUploadStatus(null);
      setTaxUploadProgress((prev) => ({
        ...prev,
        'Proof of guardianship': 0,
        'Proof of custodianship': 0,
        'Date of birth certificate': 0,
      }));
      setTaxLinearProgress((prev) => ({
        ...prev,
        'Proof of guardianship': false,
        'Proof of custodianship': false,
        'Date of birth certificate': false,
      }));
    }
    if (value.length > 0) {
      setBeneficiaryData((prevState) => ({
        ...prevState,
        otherRelation: value,
      }));
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        otherRelation: '',
      }));
    } else {
      setBeneficiaryData((prevState) => ({
        ...prevState,
        otherRelation: value,
      }));
      setBeneficiaryDataErrors((prevState) => ({
        ...prevState,
        otherRelation: MANDATORY_FIELD_ERR,
      }));
    }
  };

  const otherRelationList = isABC
    ? ['Grandparent', 'Aunt', 'Uncle', 'Cousin']
    : ['Legal Guardian', 'Custodian', 'Aunt', 'Uncle'];
  const BeneficiaryRelationCard = [
    {
      icon: parentIcon,
      text: 'Parent',
    },
    {
      icon: grandparentIcon,
      text: isABC ? 'Legal Guardian' : 'Grandparent',
    },
    {
      icon: otherIcon,
      text: isABC ? 'Other Family Member' : 'Other',
    },
  ];
  const BeneficiaryRelationStyle = styled(Card)(({ isActive, isDisable }) => ({
    ...classes.cardContent,
    ...(isActive && {
      border: `2px solid ${COLOR5}`,
      backgroundColor: '#F0E8F0',
    }),
    ...(isDisable && {
      pointerEvents: 'none',
    }),
  }));
  const EmailRecipientCard = ['Parent', 'Legal Guardian'];
  const EmailRecipientStyle = styled(Card)(({ isActive, item }) => ({
    ...classes.emailCard,
    maxWidth: item === 'Parent' ? '130px' : '150px',
    ...(isActive && {
      border: `2px solid ${COLOR5}`,
      backgroundColor: '#FFFFFF',
    }),
  }));

  return (
    <Grid size={{ xs: 12 }}>
      <Grid size={{ xs: 12, md: 12 }}>
        <InputLabel
          htmlFor="beneficiaryRelationship"
          required
          id="beneficiaryRelationship_label"
          data-testid="beneficiaryRelationship_testid"
          aria-label={`${t('Select your relationship to the beneficiary', {
            defaultValue: 'Select your relationship to the beneficiary',
          })} ${t(REQUIRED, {
            defaultValue: REQUIRED,
          })}`}
        >
          {t('Select your relationship to the beneficiary', {
            defaultValue: 'Select your relationship to the beneficiary',
          })}
        </InputLabel>
        <Grid container sx={classes.cardContainer}>
          {BeneficiaryRelationCard.map((item) => (
            <BeneficiaryRelationStyle
              key={item.text}
              tabIndex="0"
              variant="outlined"
              onClick={(e) => handleRelationChange(e, item.text)}
              data-testid="beneficiaryRelationCard_testid"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleRelationChange(e, item.text);
                }
              }}
              aria-label={
                beneficiaryData?.relationship === item.text ? `${item.text} selected` : item.text
              }
              isActive={beneficiaryData?.relationship === item.text}
              isDisable={isDisabled}
            >
              <Box component="img" src={item.icon} alt="Relation Icon" height="30px" width="30px" />
              <Typography
                sx={{
                  ...classes.cardText,
                  ...(language === EN &&
                    !isABC && {
                      '@media (max-width:750px)': {
                        maxWidth: '50px',
                        width: '100%',
                        wordWrap: 'break-word',
                      },
                    }),
                }}
              >
                {item.text === 'Other Family Member' && language === EN ? (
                  <>
                    {t('Other', { defaultValue: 'Other' })}
                    <br />
                    {t('Family Member', { defaultValue: 'Family Member' })}
                  </>
                ) : (
                  t(item.text, { defaultValue: item.text })
                )}
              </Typography>
            </BeneficiaryRelationStyle>
          ))}
        </Grid>
        <Grid size={12}>
          <Box
            component="span"
            sx={classes.errorMessage}
            role="alert"
            data-testid="beneficiaryRelationCard_errid"
          >
            {t(beneficiaryDataErrors?.relationship, {
              defaultValue: beneficiaryDataErrors?.relationship,
            })}
          </Box>
        </Grid>
      </Grid>
      {beneficiaryData?.relationship && (
        <Grid>
          {(beneficiaryData?.relationship === 'Other' ||
            beneficiaryData?.relationship === 'Other Family Member') && (
            <Grid size={{ xs: 12 }} sx={{ marginTop: '20px' }}>
              <CustomSelect
                id="otherRelation"
                dataTestId="otherRelation_id"
                required
                inputLabelAriaLabel={`${t('Specify your relationship to the beneficiary', {
                  defaultValue: 'Specify your relationship to the beneficiary',
                })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
                label={t('Specify your relationship to the beneficiary', {
                  defaultValue: 'Specify your relationship to the beneficiary',
                })}
                options={otherRelationList}
                handleChange={handleOtherRelationChange}
                fullWidth
                value={commonUtility(beneficiaryData?.otherRelation)}
                error={beneficiaryDataErrors?.otherRelation}
                selectAriaLabel={`${t('Specify your relationship to the beneficiary', {
                  defaultValue: 'Specify your relationship to the beneficiary',
                })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
                placeholder="Select Your Relationship"
                isOtherRelation
              />
              <Box
                component="span"
                role="alert"
                sx={classes.errorMessage}
                data-testid="otherRelationError"
              >
                {t(beneficiaryDataErrors?.otherRelation, {
                  defaultValue: beneficiaryDataErrors?.otherRelation,
                })}
              </Box>
            </Grid>
          )}
          {(beneficiaryData?.relationship !== 'Other' &&
            beneficiaryData?.relationship !== 'Other Family Member') ||
          beneficiaryData?.otherRelation ? (
            <Grid sx={{ width: '100%' }}>
              {beneficiaryData?.relationship !== 'Parent' &&
                beneficiaryData?.otherRelation !== 'Legal Guardian' &&
                !isABC && (
                  <Grid sx={classes.toggleContainer}>
                    <Typography sx={classes.toggleText}>
                      {t(LEGAL_GUARDIAN, {
                        defaultValue: LEGAL_GUARDIAN,
                      })}
                    </Typography>
                    <Box
                      role="application"
                      aria-roledescription={t(LEGAL_GUARDIAN, {
                        defaultValue: LEGAL_GUARDIAN,
                      })}
                    >
                      <CustomSwitch
                        id="legalGuardian"
                        ariaLabelName={`${t(LEGAL_GUARDIAN, {
                          defaultValue: LEGAL_GUARDIAN,
                        })}`}
                        defaultData={!!beneficiaryData?.legalGuardian}
                        ariaLabelledby="toggleone"
                        handleClickCheckbox={handleAuthorityToggle}
                        toggleCheckboxOnEnter={authorityToggleOnEnter}
                        dataTestId="legalGuardian"
                      />
                    </Box>
                  </Grid>
                )}
              <Grid container sx={classes.documentUploadContainer} spacing={2}>
                {!(isABC && beneficiaryData?.beneficiaryLists?.dobUploadFlag === 'false') && (
                  <Grid
                    size={{
                      xs: 12,
                      md:
                        isSmallScreen ||
                        beneficiaryData?.relationship === 'Parent' ||
                        (beneficiaryData?.otherRelation !== 'Custodian' &&
                          beneficiaryData?.legalGuardian === false) ||
                        (isABC && beneficiaryData?.relationship !== 'Legal Guardian')
                          ? 12
                          : 6,
                      lg:
                        isSmallScreen ||
                        beneficiaryData?.relationship === 'Parent' ||
                        (beneficiaryData?.otherRelation !== 'Custodian' &&
                          beneficiaryData?.legalGuardian === false) ||
                        (isABC && beneficiaryData?.relationship !== 'Legal Guardian')
                          ? 12
                          : 6,
                    }}
                  >
                    <CommonDocumentUpload
                      uploadData={birthProofUploadData}
                      setUploadData={setBirthProofUploadData}
                      uploadError={birthProofUploadError}
                      setUploadDataError={setBirthProofUploadError}
                      setUploadStatus={setBirthProofUploadStatus}
                      uploadStatus={birthProofUploadStatus}
                      uploadFileData={birthProofUploadData}
                      setUploadFileData={setBirthProofUploadData}
                      uploadDocHeading="Upload the beneficiary's birth certificate"
                      pagePath={BENEFICIARY_INFO}
                      pageProps={props}
                      fileType="Date of birth certificate"
                      toolTipData={isABC ? TOOLTIP_ABC_BIRTH_PROOF : TOOLTIP_EDUCATIVA_BIRTH_PROOF}
                      columnLayout={
                        (beneficiaryData?.relationship === 'Other' &&
                          (beneficiaryData?.legalGuardian === true ||
                            beneficiaryData?.otherRelation === 'Custodian')) ||
                        (beneficiaryData?.relationship === 'Grandparent' &&
                          beneficiaryData?.legalGuardian === true) ||
                        isTab ||
                        beneficiaryData?.relationship === 'Legal Guardian'
                      }
                    />
                    <Box
                      component="span"
                      sx={classes.errorMessage}
                      role="alert"
                      data-testid="document-upload--error"
                    >
                      {t(birthProofUploadError?.document, {
                        defaultValue: birthProofUploadError?.document,
                      })}
                    </Box>
                  </Grid>
                )}
                {((beneficiaryData?.relationship !== 'Parent' &&
                  beneficiaryData?.legalGuardian === true) ||
                  beneficiaryData?.relationship === 'Legal Guardian') && (
                  <Grid
                    size={{
                      xs: 12,
                      md:
                        (isABC && beneficiaryData?.beneficiaryLists?.dobUploadFlag === 'false') ||
                        isSmallScreen
                          ? 12
                          : 6,
                      lg:
                        (isABC && beneficiaryData?.beneficiaryLists?.dobUploadFlag === 'false') ||
                        isSmallScreen
                          ? 12
                          : 6,
                    }}
                  >
                    <CommonDocumentUpload
                      uploadData={authorityProofUploadData}
                      setUploadData={setAuthorityProofUploadData}
                      uploadError={authorityProofUploadError}
                      setUploadDataError={setAuthorityProofUploadError}
                      setUploadStatus={setAuthorityProofUploadStatus}
                      uploadStatus={authorityProofUploadStatus}
                      uploadFileData={authorityProofUploadData}
                      setUploadFileData={setAuthorityProofUploadData}
                      uploadDocHeading="Upload your legal proof of guardianship"
                      pagePath={BENEFICIARY_INFO}
                      pageProps={props}
                      fileType="Proof of guardianship"
                      toolTipData="We require this document to verify your legal authority over the beneficiary and allow account setup on their behalf."
                      columnLayout={
                        !(isABC && beneficiaryData?.beneficiaryLists?.dobUploadFlag === 'false') ||
                        isTab
                      }
                    />
                    <Box
                      component="span"
                      sx={classes.errorMessage}
                      role="alert"
                      data-testid="document-upload--error"
                    >
                      {t(authorityProofUploadError?.document, {
                        defaultValue: authorityProofUploadError?.document,
                      })}
                    </Box>
                  </Grid>
                )}
                {beneficiaryData?.otherRelation === 'Custodian' &&
                  beneficiaryData?.legalGuardian === false && (
                    <Grid size={{ xs: 12, md: isSmallScreen ? 12 : 6, lg: isSmallScreen ? 12 : 6 }}>
                      <CommonDocumentUpload
                        uploadData={authorityProofUploadData}
                        setUploadData={setAuthorityProofUploadData}
                        uploadError={authorityProofUploadError}
                        setUploadDataError={setAuthorityProofUploadError}
                        setUploadStatus={setAuthorityProofUploadStatus}
                        uploadStatus={authorityProofUploadStatus}
                        uploadFileData={authorityProofUploadData}
                        setUploadFileData={setAuthorityProofUploadData}
                        uploadDocHeading="Upload legal proof of your custody"
                        pagePath={BENEFICIARY_INFO}
                        pageProps={props}
                        fileType="Proof of custodianship"
                        toolTipData="We require this document to verify your legal authority over the beneficiary and allow account setup on their behalf."
                        columnLayout
                      />
                      <Box
                        component="span"
                        sx={classes.errorMessage}
                        role="alert"
                        data-testid="document-upload--error"
                      >
                        {t(authorityProofUploadError?.document, {
                          defaultValue: authorityProofUploadError?.document,
                        })}
                      </Box>
                    </Grid>
                  )}
                {beneficiaryData?.legalGuardian === false && (
                  <Grid sx={classes.caeContainer}>
                    <Typography sx={classes.beneficiaryText1}>
                      {t('CAE Authorization Form', {
                        defaultValue: 'CAE Authorization Form',
                      })}
                    </Typography>
                    <Grid sx={classes.caeSubHeading}>
                      <Box
                        component="img"
                        src={caeInfoIcon}
                        alt="CAE Info Icon"
                        height="20px"
                        width="20px"
                      />
                      <Typography sx={classes.caeSubText}>
                        {t(
                          'To complete the opening of the IRA Educativa, we’ll need the authorization and signature of the parent or legal guardian.',
                          {
                            defaultValue:
                              'To complete the opening of the IRA Educativa, we’ll need the authorization and signature of the parent or legal guardian.',
                          }
                        )}
                      </Typography>
                    </Grid>
                    <InputLabel
                      htmlFor="emailRecipient"
                      required
                      id="emailRecipient_label"
                      data-testid="emailRecipient_testid"
                      aria-label={`${t('Send email to', {
                        defaultValue: 'Send email to',
                      })} ${t(REQUIRED, {
                        defaultValue: REQUIRED,
                      })}`}
                    >
                      {t('Send email to', {
                        defaultValue: 'Send email to',
                      })}
                    </InputLabel>
                    <Grid container sx={classes.emailCardContainer}>
                      {EmailRecipientCard.map((item) => (
                        <EmailRecipientStyle
                          key={item}
                          tabIndex="0"
                          variant="outlined"
                          onClick={(e) => handleRecipientChange(e, item)}
                          data-testid="emailRecipientCard_testid"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleRecipientChange(e, item);
                            }
                          }}
                          aria-label={
                            beneficiaryData?.caeData?.emailRecipient === item
                              ? `${item} selected`
                              : item
                          }
                          isActive={beneficiaryData?.caeData?.emailRecipient === item}
                          item={item}
                        >
                          <Typography sx={classes.caeCard}>
                            {t(item, { defaultValue: item })}
                          </Typography>
                        </EmailRecipientStyle>
                      ))}
                    </Grid>
                    <Box
                      component="span"
                      sx={classes.errorMessage}
                      role="alert"
                      data-testid="emailRecipientCard_errid"
                    >
                      {t(beneficiaryDataErrors?.caeData?.emailRecipient, {
                        defaultValue: beneficiaryDataErrors?.caeData?.emailRecipient,
                      })}
                    </Box>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ marginTop: '20px' }}>
                      <CustomInput
                        id="recipientEmailAddress"
                        dataTestId="recipientEmailAddress_id"
                        required
                        inputLabelAriaLabel={`${t(EMAIL, {
                          defaultValue: EMAIL,
                        })} ${t(REQUIRED, { defaultValue: REQUIRED })}`}
                        label={t(EMAIL, {
                          defaultValue: EMAIL,
                        })}
                        handleChange={handleEmailChange}
                        placeholder={t('Enter Email Address', {
                          defaultValue: 'Enter Email Address',
                        })}
                        fullWidth
                        inputProps={{ 'data-testid': 'email', maxLength: 40 }}
                        value={
                          beneficiaryData?.caeData?.emailAddress
                            ? beneficiaryData?.caeData?.emailAddress
                            : ''
                        }
                        error={
                          beneficiaryDataErrors?.caeData?.emailAddress
                            ? beneficiaryDataErrors?.caeData?.emailAddress
                            : ''
                        }
                      />
                      <Box
                        component="span"
                        role="alert"
                        sx={classes.errorMessage}
                        data-testid="recipientEmailAddress-error"
                      >
                        {t(beneficiaryDataErrors?.caeData?.emailAddress, {
                          defaultValue: beneficiaryDataErrors?.caeData?.emailAddress,
                        })}
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </Grid>
              {!isABC && (
                <Grid container>
                  <BeneficiaryAddressInfo />
                </Grid>
              )}
            </Grid>
          ) : (
            ''
          )}
        </Grid>
      )}
    </Grid>
  );
};
export default BeneficiaryCardComp;

BeneficiaryCardComp.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
};
